// The Money form — used to ADD a new entry and to EDIT one that was saved
// wrong. Both live here so the two can never drift apart: the same kind rules,
// category chips and schedule fields apply either way.
//
// Pass `initial` (a money row from the API) to edit; omit it to add.

import React, { useEffect, useRef, useState } from 'react';
import { FiCheck, FiChevronDown } from 'react-icons/fi';

const fmt = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString('en-IN') : value || '0';
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const WEEKDAYS = [
  { v: 0, l: 'Monday' },
  { v: 1, l: 'Tuesday' },
  { v: 2, l: 'Wednesday' },
  { v: 3, l: 'Thursday' },
  { v: 4, l: 'Friday' },
  { v: 5, l: 'Saturday' },
  { v: 6, l: 'Sunday' },
];
// The two shapes of money this section holds. Chit/borrow money is RECEIVED and
// repaid until it's cleared; salary and rent are bills that just repeat. They
// used to live in two screens (Chit/Money vs Planned Payments) that modelled the
// same money differently — a chit filed as a "planned payment" had no principal
// and billed forever.
const KINDS = [
  { v: 'finite', l: 'Money to repay', hint: 'Chit or borrow — money came in, repaid until cleared' },
  { v: 'recurring', l: 'Repeating payment', hint: 'Salary, rent, EMI — goes out every week/month, no end' },
];

const CATEGORIES = [
  { v: 'chit', l: 'Chit', kinds: ['finite'] },
  { v: 'borrow', l: 'Borrowed', kinds: ['finite'] },
  { v: 'emi', l: 'EMI / Loan', kinds: ['finite', 'recurring'] },
  { v: 'salary', l: 'Salary', kinds: ['recurring'] },
  { v: 'rent', l: 'Rent', kinds: ['recurring'] },
  { v: 'other', l: 'Other', kinds: ['finite', 'recurring'] },
];
// Custom weekday dropdown — same look as the ComboBox on Products, compact to
// match this form's inputs (no search; only 7 options).
const WeekdayDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  const selected = WEEKDAYS.find((d) => d.v === value);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex w-full items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2 text-sm outline-none transition ${
          open ? 'border-violet-400 ring-2 ring-violet-200' : 'border-slate-200 hover:border-violet-300'
        }`}
      >
        <span className="truncate text-slate-800">{selected ? selected.l : 'Select day'}</span>
        <FiChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-200 ${open ? 'rotate-180 text-violet-500' : 'text-slate-400'}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1.5 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 animate-fade-in-up">
          <ul role="listbox" className="max-h-56 overflow-y-auto py-1">
            {WEEKDAYS.map((d) => {
              const isSelected = d.v === value;
              return (
                <li key={d.v} role="option" aria-selected={isSelected}>
                  <button
                    type="button"
                    onClick={() => { onChange(d.v); setOpen(false); }}
                    className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors ${
                      isSelected ? 'bg-violet-600 font-semibold text-white' : 'text-slate-700 hover:bg-violet-50 hover:text-violet-700'
                    }`}
                  >
                    <span className="truncate">{d.l}</span>
                    {isSelected && <FiCheck className="h-3.5 w-3.5 shrink-0" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
const num = (v) => (v === null || v === undefined || v === '' ? '' : String(Number(v)));

const MoneyForm = ({ onSave, onCancel, busy, initial = null }) => {
  const editing = Boolean(initial);
  const [kind, setKind] = useState(initial?.kind || 'finite');
  const [category, setCategory] = useState(initial?.category || 'chit');
  const [name, setName] = useState(initial?.name || '');
  const [source, setSource] = useState(initial?.source || '');
  const [received, setReceived] = useState(num(initial?.received_amount));
  const [principal, setPrincipal] = useState(num(initial?.principal));
  const [frequency, setFrequency] = useState(initial?.frequency || 'weekly');
  const [startDate, setStartDate] = useState(initial?.start_date || todayIso());
  const [dueInDays, setDueInDays] = useState(initial?.due_in_days ?? '');
  const [installmentAmount, setInstallmentAmount] = useState(num(initial?.installment_amount));
  const [installmentCount, setInstallmentCount] = useState(initial?.installment_count ?? '');
  const [weekday, setWeekday] = useState(initial?.weekday ?? 3); // Thursday — common chit day
  const [monthDay, setMonthDay] = useState(String(initial?.month_day ?? '5'));
  const [note, setNote] = useState(initial?.note || '');
  const [err, setErr] = useState('');

  const recurring = kind === 'recurring';

  // Picking a kind sets a sensible default the other fields follow from: a
  // repeating bill can't be a one-off, and chit/borrow default to finite.
  const pickKind = (k) => {
    setKind(k);
    setErr('');
    if (k === 'recurring') {
      if (frequency === 'days') setFrequency('monthly');
      if (['chit', 'borrow'].includes(category)) setCategory('salary');
    } else if (['salary', 'rent'].includes(category)) {
      setCategory('chit');
    }
  };

  const submit = () => {
    if (!name.trim()) { setErr('Enter a name'); return; }
    if (!startDate) { setErr('Pick a start date'); return; }

    const body = {
      name: name.trim(),
      kind,
      category,
      frequency,
      start_date: startDate,
      source: source.trim() || undefined,
      note: note.trim() || undefined,
    };

    if (recurring) {
      // A bill: no principal, no end — just what goes out each period.
      const ia = Number(installmentAmount);
      if (!Number.isFinite(ia) || ia <= 0) { setErr('Enter what goes out each period'); return; }
      body.installment_amount = ia;
      if (frequency === 'weekly') body.weekday = Number(weekday);
      if (frequency === 'monthly') {
        const md = Number(monthDay);
        if (!Number.isFinite(md) || md < 1 || md > 31) { setErr('Day of month must be 1–31'); return; }
        body.month_day = md;
      }
      setErr('');
      onSave(body);
      return;
    }

    const p = Number(principal);
    if (!Number.isFinite(p) || p <= 0) { setErr('Enter a valid amount to repay'); return; }
    body.principal = p;
    if (received !== '') {
      const r = Number(received);
      if (!Number.isFinite(r) || r < 0) { setErr('Enter a valid received amount'); return; }
      body.received_amount = r;
    }

    if (frequency === 'days') {
      const d = Number(dueInDays);
      if (!Number.isFinite(d) || d <= 0) { setErr('Enter how many days to repay in'); return; }
      body.due_in_days = d;
    } else {
      const ia = Number(installmentAmount);
      const ic = Number(installmentCount);
      if (installmentAmount !== '' && (!Number.isFinite(ia) || ia <= 0)) { setErr('Enter a valid installment amount'); return; }
      if (installmentCount !== '' && (!Number.isFinite(ic) || ic <= 0)) { setErr('Enter a valid installment count'); return; }
      if (installmentAmount !== '') body.installment_amount = ia;
      if (installmentCount !== '') body.installment_count = ic;
      if (frequency === 'weekly') body.weekday = Number(weekday);
      if (frequency === 'monthly') {
        const md = Number(monthDay);
        if (!Number.isFinite(md) || md < 1 || md > 31) { setErr('Day of month must be 1–31'); return; }
        body.month_day = md;
      }
    }

    setErr('');
    onSave(body);
  };

  const inputCls = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-200';

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      {/* Which shape of money — everything else follows from this */}
      <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {KINDS.map((k) => (
          <button
            key={k.v}
            type="button"
            onClick={() => pickKind(k.v)}
            className={`rounded-xl border-2 p-3 text-left transition-all ${kind === k.v ? 'border-violet-500 bg-violet-50' : 'border-slate-200 bg-white hover:border-violet-300'}`}
          >
            <p className={`text-sm font-bold ${kind === k.v ? 'text-violet-700' : 'text-slate-700'}`}>{k.l}</p>
            <p className="mt-0.5 text-[11px] text-slate-500">{k.hint}</p>
          </button>
        ))}
      </div>

      <div className="mb-3 flex flex-wrap gap-1.5">
        {CATEGORIES.filter((c) => c.kinds.includes(kind)).map((c) => (
          <button key={c.v} type="button" onClick={() => setCategory(c.v)}
            className={`rounded-full border px-3 py-1.5 text-xs font-bold transition ${category === c.v ? 'border-violet-500 bg-violet-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}>
            {c.l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input value={name} onChange={(e) => setName(e.target.value)}
          placeholder={recurring ? 'Name (e.g. Ravi salary, Shop rent)' : 'Name (e.g. Kunja borrow, Chit #3)'}
          className={inputCls} />
        <input value={source} onChange={(e) => setSource(e.target.value)}
          placeholder={recurring ? 'Paid to (optional)' : 'Source / lender (optional)'} className={inputCls} />
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {recurring ? (
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Amount each {frequency === 'weekly' ? 'week' : 'month'}
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">₹</span>
              <input value={installmentAmount ? fmt(installmentAmount) : ''}
                onChange={(e) => setInstallmentAmount(e.target.value.replace(/[^\d]/g, ''))}
                type="text" inputMode="numeric" placeholder="e.g. 20,000"
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-8 pr-3 text-sm outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-200" />
            </div>
            <p className="mt-1 text-[11px] text-slate-400">It repeats forever — there's no total to clear.</p>
          </div>
        ) : (
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Total to repay</label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">₹</span>
              <input value={principal ? fmt(principal) : ''} onChange={(e) => setPrincipal(e.target.value.replace(/[^\d]/g, ''))}
                type="text" inputMode="numeric" placeholder="e.g. 5,00,000"
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-8 pr-3 text-sm outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-200" />
            </div>
          </div>
        )}
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Start date</label>
          <input value={startDate} onChange={(e) => setStartDate(e.target.value)} type="date" className={inputCls} />
        </div>
      </div>

      {/* Money that actually came IN — a chit payout or a borrow received */}
      {!recurring && (
        <div className="mt-3 sm:w-1/2 sm:pr-1.5">
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Money received (optional)
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">₹</span>
            <input value={received ? fmt(received) : ''} onChange={(e) => setReceived(e.target.value.replace(/[^\d]/g, ''))}
              type="text" inputMode="numeric" placeholder="e.g. 50,00,000"
              className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-8 pr-3 text-sm outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-200" />
          </div>
          <p className="mt-1 text-[11px] text-slate-400">What came into the business — a chit payout or a borrow.</p>
        </div>
      )}

      {/* Frequency toggle — a repeating bill can't be a one-off */}
      <div className="mt-3 flex rounded-lg border border-slate-200 bg-white p-0.5 w-fit">
        {[{ v: 'days', l: 'Days' }, { v: 'weekly', l: 'Weekly' }, { v: 'monthly', l: 'Monthly' }]
          .filter((f) => !(recurring && f.v === 'days'))
          .map((f) => (
            <button key={f.v} type="button" onClick={() => setFrequency(f.v)}
              className={`rounded-md px-3 py-1.5 text-xs font-bold transition ${frequency === f.v ? 'bg-violet-600 text-white' : 'text-slate-500'}`}>
              {f.l}
            </button>
          ))}
      </div>

      {/* Conditional schedule fields */}
      {frequency === 'days' && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Repay in (days)</label>
            <input value={dueInDays} onChange={(e) => setDueInDays(e.target.value)} type="number" min="1" placeholder="e.g. 10" className={inputCls} />
          </div>
        </div>
      )}

      {/* A repeating bill already gave its amount above and has no instalment
          run — it only needs the day it falls on. */}
      {frequency === 'weekly' && (
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {!recurring && (
            <>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Installment ₹</label>
                <input value={installmentAmount ? fmt(installmentAmount) : ''} onChange={(e) => setInstallmentAmount(e.target.value.replace(/[^\d]/g, ''))} type="text" inputMode="numeric" placeholder="e.g. 50,000" className={inputCls} />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400"># Installments</label>
                <input value={installmentCount} onChange={(e) => setInstallmentCount(e.target.value)} type="number" min="1" placeholder="e.g. 10" className={inputCls} />
              </div>
            </>
          )}
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Day of week</label>
            <WeekdayDropdown value={Number(weekday)} onChange={(v) => setWeekday(v)} />
          </div>
        </div>
      )}

      {frequency === 'monthly' && (
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {!recurring && (
            <>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Installment ₹</label>
                <input value={installmentAmount ? fmt(installmentAmount) : ''} onChange={(e) => setInstallmentAmount(e.target.value.replace(/[^\d]/g, ''))} type="text" inputMode="numeric" placeholder="e.g. 20,000" className={inputCls} />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400"># Installments</label>
                <input value={installmentCount} onChange={(e) => setInstallmentCount(e.target.value)} type="number" min="1" placeholder="e.g. 12" className={inputCls} />
              </div>
            </>
          )}
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Day of month</label>
            <input value={monthDay} onChange={(e) => setMonthDay(e.target.value)} type="number" min="1" max="31" placeholder="1–31" className={inputCls} />
          </div>
        </div>
      )}

      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className={`mt-3 ${inputCls}`} />
      {err && <p className="mt-2 text-xs font-semibold text-rose-500">{err}</p>}
      <div className="mt-3 flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={busy} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600">Cancel</button>
        <button type="button" onClick={submit} disabled={busy} className="rounded-lg bg-violet-600 px-3 py-2 text-xs font-bold text-white disabled:opacity-50">
          {busy ? 'Saving…' : editing ? 'Save changes' : recurring ? 'Add payment' : 'Add money'}
        </button>
      </div>
    </div>
  );
};

export default MoneyForm;
export { KINDS, CATEGORIES, WEEKDAYS };
